/** Score a user-authored trigger against its root forecast without coupling it to the branch run. */

import { massOf, validateDecomposition } from "@sooth-labs/idl";

import type { components } from "../../types/forecastServer";

import type { TriggerContract } from "./triggerContract";
import { TOWER_TOLERANCE } from "./towerTolerance.js";

export type TriggerScore = components["schemas"]["TriggerScoreResponse"];

type Post = (
  url: string,
  body: unknown,
  options?: { signal?: AbortSignal | null; headers?: Record<string, string> },
) => Promise<{ ok: boolean; status: number; body: unknown }>;

interface ParentLike {
  contractKey?: string | null;
  qkey?: string | null;
  traceId?: string | null;
  trace_id?: string | null;
  runKey?: string | null;
  run_key?: string | null;
  reportPointers?: unknown[] | null;
  contract?: {
    question?: string | null;
    title?: string | null;
    resolution_criteria?: string | null;
    resolution_date?: string | null;
    background?: string | null;
  } | null;
}

const TRIGGER_SCORE_TIMEOUT_MS = 20_000;
const TRIGGER_SCORE_DEADLINE_HEADER = "x-trigger-score-deadline-ms";
const TRIGGER_SCORE_MODEL = "openrouter/google/gemini-3.1-flash-lite";
const TRIGGER_SCORE_MOVER_FLOOR = 0.02;

function failedScore(): TriggerScore {
  return {
    status: "failed",
    reason: "llm",
    w: null,
    a: null,
    b: null,
    direction: null,
    p_conditional: null,
    residual: null,
    attempts: 0,
    problems: ["request_failed"],
    rationale: null,
    model: TRIGGER_SCORE_MODEL,
  };
}

function isTriggerScore(value: unknown, p0: number | null): value is TriggerScore {
  if (!value || typeof value !== "object") return false;
  const score = value as Record<string, unknown>;
  if (
    typeof score.model !== "string" ||
    typeof score.attempts !== "number" ||
    ![0, 1, 2].includes(score.attempts) ||
    (score.problems !== undefined &&
      (!Array.isArray(score.problems) ||
        score.problems.some((problem) => typeof problem !== "string")))
  ) {
    return false;
  }
  if (score.status === "failed") return score.reason === "llm";
  if (score.status === "unscored") {
    return (
      typeof score.reason === "string" &&
      ["multiway", "window", "incoherent", "non_mover"].includes(score.reason)
    );
  }
  const { w, a, b, p_conditional, direction, residual } = score;
  if (
    score.status !== "coherent" ||
    score.attempts === 0 ||
    p0 === null ||
    !isProbability(w) ||
    !isProbability(a) ||
    !isProbability(b) ||
    !isProbability(p_conditional) ||
    !isProbability(residual) ||
    w === 0 ||
    w === 1 ||
    Math.abs(a - b) < TRIGGER_SCORE_MOVER_FLOOR
  ) {
    return false;
  }

  const headline = { form: "bernoulli" as const, probability: p0 };
  const triple = {
    p_occurs: w,
    if_occurs: { form: "bernoulli" as const, probability: a },
    if_not: { form: "bernoulli" as const, probability: b },
  };
  try {
    validateDecomposition(headline, triple, TOWER_TOLERANCE);
  } catch {
    return false;
  }
  const occurs = massOf(triple.if_occurs);
  const otherwise = massOf(triple.if_not);
  // The server reports the maximum across both slots, including floating-point residuals.
  const expectedResidual = Math.max(
    ...massOf(headline).map((expected, i) =>
      Math.abs(w * occurs[i]! + (1 - w) * otherwise[i]! - expected),
    ),
  );
  const expectedDirection = a > p0 ? "yes" : "no";
  return (
    direction === expectedDirection &&
    p_conditional === (expectedDirection === "yes" ? a : 1 - a) &&
    residual === expectedResidual
  );
}

function isProbability(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
}

function runKeyFromPointers(parent: ParentLike, qkey: string): string | null {
  if (!Array.isArray(parent.reportPointers) || !qkey) return null;
  const suffix = `#${qkey}`;
  for (let i = parent.reportPointers.length - 1; i >= 0; i -= 1) {
    const pointer = parent.reportPointers[i];
    if (!pointer || typeof pointer !== "object") continue;
    const rowKey = (pointer as { kind?: unknown; row_key?: unknown }).row_key;
    if (
      (pointer as { kind?: unknown }).kind === "forecast" &&
      typeof rowKey === "string" &&
      rowKey.endsWith(suffix)
    ) {
      return rowKey.slice(0, -suffix.length) || null;
    }
  }
  return null;
}

/**
 * Call the bounded scorer and always return a score object.
 *
 * The scored triple annotates the root-timeline fork event only. It is deliberately
 * separate from the branch request and is never used as branch soft context.
 */
export async function scoreTypedTrigger(args: {
  parent: ParentLike;
  p0: number | null | undefined;
  report?: unknown;
  contract: TriggerContract;
  placedOn: string;
  outcomeSpace?: Record<string, unknown> | null;
  signal?: AbortSignal | null;
  post?: Post;
}): Promise<TriggerScore> {
  const post = args.post || (await import("../apiClient.js")).apiPost;
  const qkey = args.parent.contractKey || args.parent.qkey || "";
  const traceId = args.parent.traceId || args.parent.trace_id || "";
  if (!qkey || !traceId) return failedScore();
  // Multiway requests return unscored before the server reads this sentinel.
  const p0 = args.outcomeSpace ? 0 : args.p0;
  if (!isProbability(p0)) return failedScore();

  const deadlineMs = Date.now() + TRIGGER_SCORE_TIMEOUT_MS;
  const timeout = AbortSignal.timeout(TRIGGER_SCORE_TIMEOUT_MS);
  const signal = args.signal ? AbortSignal.any([args.signal, timeout]) : timeout;
  const parentContract = args.parent.contract || {};
  const runKey =
    args.parent.runKey || args.parent.run_key || runKeyFromPointers(args.parent, qkey) || null;
  const body: components["schemas"]["TriggerScoreRequest"] = {
    question: {
      question_text: parentContract.question || parentContract.title || "",
      resolution_criteria: parentContract.resolution_criteria || "",
      resolution_date: parentContract.resolution_date || "",
      background: parentContract.background || null,
    },
    p0,
    report: (args.report || null) as components["schemas"]["TriggerScoreReport"] | null,
    trigger: {
      question: args.contract.question,
      resolution_criteria: args.contract.resolution_criteria,
      resolution_date: args.contract.resolution_date,
      assertion: args.contract.assertion || null,
      placed_on: args.placedOn.slice(0, 10),
    },
    qkey,
    parent: { trace_id: traceId, run_key: runKey },
    ...(args.outcomeSpace ? { outcome_space: args.outcomeSpace } : {}),
  };

  try {
    const response = await post("/api/forecast/triggers/score", body, {
      signal,
      headers: { [TRIGGER_SCORE_DEADLINE_HEADER]: String(deadlineMs) },
    });
    return response.ok && isTriggerScore(response.body, args.outcomeSpace ? null : p0)
      ? response.body
      : failedScore();
  } catch {
    return failedScore();
  }
}
